/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useContext, useEffect, useState } from 'react';

import { MOMENT_FORMATS, formatDateTimeDisplay, momentWithUserTimezone } from 'js/utils/DateTimeUtils';

import { Button, Grid, RadioCard, Typography2 } from '@coursera/cds-core';
import { AddIcon, EditIcon } from '@coursera/cds-icons';

// FIXME: existing import/no-cycle violations are excused to prevent seeing errors when modifying other parts of the same file; please fix it carefully
// eslint-disable-next-line import/no-cycle
import { PrivateSessionDrawerContext } from 'bundles/enterprise-admin-drawers/components/PrivateSessionEditDrawerContent';
import SessionWithDateInput from 'bundles/enterprise-admin-drawers/components/elements/SessionWithDateInput';
import type { DateRangeValue } from 'bundles/enterprise-ui/components/DateRangeInput';

import _t from 'i18n!nls/admin-dashboard';

type RadioOptionWithDateProps = {
  startsAt: number;
  endsAt: number;
  sessionId: string;
  name?: string;
  showCustomDateInputs?: boolean;
};

type EditableSessionProps = {
  maxEndDate?: number;
  error?: string | React.ReactNode;
  onChangeDate: (date: DateRangeValue) => void;
  onChangeName: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

type CustomAvailabilityOptionProps = {
  startsAt: number;
  endsAt: number;
  name?: string;
  showCustomDateInputs: boolean;
  onRadioSelected: (e: React.ChangeEvent<HTMLInputElement>) => void;
};

export const FlexibleOption: React.FC = () => (
  <RadioCard
    label={_t('Always available')}
    value="flexibleOption"
    supportText={_t('Learners can start the course anytime and complete the course anytime')}
    data-testid="flexible-option-schedule-radio-button"
  />
);

const labelStyle = css`
  width: 100%;
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;
const labelTextStyle = css`
  flex-grow: 1;
`;

const iconButtonStyle = css`
  padding: 0 !important;
`;

export const ExternallyManagedOption: React.FC<
  RadioOptionWithDateProps & EditableSessionProps & { canEdit: boolean; isSelected: boolean }
> = ({ startsAt, endsAt, name, sessionId, canEdit, isSelected, maxEndDate, error, onChangeName, onChangeDate }) => {
  const [editing, setEditing] = useState(false);
  const { isSelectionDisabled } = useContext(PrivateSessionDrawerContext);

  useEffect(() => {
    setEditing(false);
  }, [isSelected]);

  const renderLabel = () => {
    return (
      <Grid css={labelStyle}>
        <Typography2 variant="bodySecondary" component="span" css={labelTextStyle}>
          {_t('Limited availability: #{start}-#{end}', {
            start: formatDateTimeDisplay(startsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY),
            end: formatDateTimeDisplay(endsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY),
          })}
        </Typography2>
        {!isSelectionDisabled && canEdit && isSelected && !editing ? (
          <Button
            css={iconButtonStyle}
            onClick={() => setEditing(true)}
            data-testid="externally-managed-option-edit-button"
            variant="ghost"
            size="small"
            icon={<EditIcon />}
            iconPosition="before"
          >
            Edit
          </Button>
        ) : undefined}
      </Grid>
    );
  };

  return (
    <RadioCard
      data-testid="limited-availablity-schedule-radio-button"
      //@ts-expect-error Disable error until we have a renderLabel property on RadioCard.
      label={renderLabel()}
      value={`externallyManagedSessionOption~${sessionId}`}
      supportText={name || ''}
    >
      {editing && (
        <SessionWithDateInput
          disabled={false}
          date={{
            startDate: momentWithUserTimezone(startsAt).format(),
            endDate: momentWithUserTimezone(endsAt).format(),
          }}
          name={name || ''}
          onChangeDate={onChangeDate}
          onChangeName={onChangeName}
          maxEndDate={maxEndDate}
          error={error}
        />
      )}
    </RadioCard>
  );
};

export const PrivateSessionGroupOption: React.FC<RadioOptionWithDateProps> = ({
  startsAt,
  endsAt,
  name,
  sessionId,
}) => (
  <RadioCard
    data-testid="private-session-group-option-schedule-radio-button"
    label={_t('Limited availability: #{start}-#{end}', {
      start: formatDateTimeDisplay(startsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY),
      end: formatDateTimeDisplay(endsAt, MOMENT_FORMATS.MED_DATE_ONLY_DISPLAY),
    })}
    value={`privateSessionGroupOption~${sessionId}`}
    supportText={name || ''}
  />
);

const newButtonDivStyle = css`
  padding-bottom: var(--cds-spacing-200);
`;

export const CustomAvailabilityOption: React.FC<CustomAvailabilityOptionProps & EditableSessionProps> = ({
  name,
  showCustomDateInputs,
  startsAt,
  endsAt,
  error,
  maxEndDate,
  onChangeDate,
  onChangeName,
  onRadioSelected,
}) => {
  const [isEditing, setIsEditing] = useState(false);
  const simulateChangeEvent = () => {
    setIsEditing(true);
    const event = {
      target: {
        value: 'createExternallyManagedSelection',
      },
    } as React.ChangeEvent<HTMLInputElement>;

    onRadioSelected(event);
  };

  if (isEditing)
    return (
      <RadioCard
        label={_t('Add custom availability')}
        value="createExternallyManagedSelection"
        data-testid="externally-managed-option-schedule-radio-button"
        supportText={_t('Learners must start and end the course within the specified period')}
      >
        {showCustomDateInputs && (
          <SessionWithDateInput
            disabled={false}
            date={{
              startDate: momentWithUserTimezone(startsAt).format(),
              endDate: momentWithUserTimezone(endsAt).format(),
            }}
            name={name || ''}
            error={error}
            onChangeDate={onChangeDate}
            onChangeName={onChangeName}
            maxEndDate={maxEndDate}
          />
        )}
      </RadioCard>
    );
  else
    return (
      <div css={newButtonDivStyle}>
        <Button
          onClick={simulateChangeEvent}
          data-testid="custom-availability-option-edit-button"
          variant="ghost"
          icon={<AddIcon />}
          iconPosition="before"
          fullWidth
        >
          {_t('Add custom limited availability')}
        </Button>
      </div>
    );
};
